import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Send, Image as ImageIcon, ArrowLeft, MessageSquare, Search, X, Loader2, Lock, ArrowRight, Sparkles, Users } from "lucide-react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { api, fileUrl, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Seo from "../components/Seo";
import ImageLightbox from "../components/ImageLightbox";

const POLL_MS = 5000;

/* Small avatar */
function Avatar({ user, size = 36 }) {
    const name = user?.name || "?";
    const initials = name.split(/\s+/).map((s) => s[0]).slice(0, 2).join("").toUpperCase();
    const src = user?.profile_image ? fileUrl(user.profile_image) : null;
    return src ? (
        <img src={src} alt={name} className="rounded-full object-cover" style={{ width: size, height: size }} />
    ) : (
        <div
            className="rounded-full bg-gradient-to-br from-navy-500 to-navy-700 text-white font-bold flex items-center justify-center shrink-0"
            style={{ width: size, height: size, fontSize: size / 2.6 }}
        >
            {initials}
        </div>
    );
}

/* NOTE: Chat page no longer hard-blocks free users. FREE 1-on-1 chat with Admin/Support
   is enabled for everyone; DMs with other users still require an active paid plan
   (backend enforces + returns 402). We surface the paid gate lazily via toast on 402. */

/* Modal to search + start a new conversation */
function NewChatModal({ open, onClose, onStart }) {
    const [q, setQ] = useState("");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open) return;
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, [open]);

    useEffect(() => {
        if (!open || q.trim().length < 2) { setItems([]); return; }
        setLoading(true);
        const id = setTimeout(async () => {
            try {
                const { data } = await api.get(`/chat/users/search?q=${encodeURIComponent(q.trim())}`);
                setItems(Array.isArray(data) ? data : []);
            } catch { /* silent */ }
            finally { setLoading(false); }
        }, 250);
        return () => clearTimeout(id);
    }, [q, open]);

    if (!open) return null;

    return createPortal(
        <div
            className="fixed inset-0 z-[10000] bg-navy-950/80 backdrop-blur-sm flex items-end sm:items-center justify-center sm:p-4"
            onClick={onClose}
            data-testid="chat-newchat-modal"
        >
            <div
                className="w-full sm:max-w-md bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl overflow-hidden max-h-[85vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="shrink-0 flex items-center justify-between px-5 py-4 border-b border-navy-500/10">
                    <h3 className="font-heading text-lg font-bold text-navy-500">Start a new chat</h3>
                    <button onClick={onClose} className="text-navy-500/50 hover:text-navy-500"><X className="w-5 h-5" /></button>
                </div>
                <div className="shrink-0 px-5 py-3 border-b border-navy-500/10">
                    <div className="relative">
                        <Search className="absolute left-3 top-2.5 w-4 h-4 text-navy-500/40" />
                        <input
                            autoFocus
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Search by name, city, profession or company…"
                            data-testid="chat-newchat-search"
                            className="w-full pl-9 pr-3 py-2 rounded-md border border-navy-500/20 text-sm focus:outline-none focus:border-gold-500/60"
                        />
                    </div>
                    <div className="text-[10px] uppercase tracking-widest text-navy-500/45 mt-2">
                        Search brokers, builders, advocates, loan agents, architects, contractors & more
                    </div>
                </div>
                <div className="overflow-y-auto">
                    {loading && (
                        <div className="p-6 text-center text-navy-500/60 text-sm">Searching…</div>
                    )}
                    {!loading && q.trim().length >= 2 && items.length === 0 && (
                        <div className="p-6 text-center text-navy-500/50 text-sm">No users found.</div>
                    )}
                    {!loading && q.trim().length < 2 && (
                        <div className="p-6 text-center text-navy-500/40 text-sm">Type at least 2 letters to search…</div>
                    )}
                    {items.map((u) => {
                        const meta = [u.company_name, u.profession, u.city].filter(Boolean).join(" • ");
                        return (
                            <button
                                key={u.id}
                                onClick={() => { onStart(u); onClose(); }}
                                data-testid={`chat-newchat-user-${u.id}`}
                                className="w-full text-left px-5 py-3 flex items-center gap-3 hover:bg-gold-500/5 border-b border-navy-500/5"
                            >
                                <Avatar user={u} size={40} />
                                <div className="min-w-0 flex-1">
                                    <div className="font-bold text-navy-500 text-sm truncate">{u.name}</div>
                                    <div className="text-[11px] text-navy-500/60 uppercase tracking-wider">{u.role}</div>
                                    {meta && (
                                        <div className="text-[11px] text-navy-500/50 truncate mt-0.5">{meta}</div>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>
        </div>,
        document.body
    );
}

/* Conversation list — left column on desktop, full page on mobile when no conv open */
function ConversationList({ items, total, activeId, onOpen, onNewChat, loading, loadingMore, onLoadMore, communityUnread, onStartSupport }) {
    // Infinite scroll — sentinel div fires onLoadMore when it enters the viewport
    const sentinelRef = useRef(null);
    useEffect(() => {
        const el = sentinelRef.current;
        if (!el || !onLoadMore) return undefined;
        const io = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting)) onLoadMore();
        }, { root: el.parentElement, rootMargin: "0px 0px 200px 0px" });
        io.observe(el);
        return () => io.disconnect();
    }, [onLoadMore, items.length]);

    return (
        <div className="w-full md:w-80 md:border-r border-navy-500/10 bg-white flex flex-col h-full min-h-0" data-testid="chat-conv-list">
            <div className="shrink-0 p-4 border-b border-navy-500/10 flex items-center justify-between">
                <h1 className="font-heading text-lg font-bold text-navy-500 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-gold-500" /> Chats
                    {typeof total === "number" && total > 0 && (
                        <span data-testid="chat-conv-total" className="text-[10px] uppercase tracking-widest text-navy-500/45 font-bold">· {total}</span>
                    )}
                </h1>
                <button
                    onClick={onNewChat}
                    data-testid="chat-new-btn"
                    className="text-xs px-3 py-1.5 rounded-full bg-gold-500 text-navy-500 font-extrabold hover:bg-gold-400"
                >
                    + New
                </button>
            </div>
            {/* Free Support entry — every logged-in user gets 1-tap access to Admin/Support chat */}
            <button
                onClick={onStartSupport}
                data-testid="chat-support-entry"
                className="shrink-0 w-full text-left px-4 py-3 flex items-center gap-3 border-b border-navy-500/10 bg-emerald-500/5 hover:bg-emerald-500/10 transition-colors"
            >
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shrink-0 shadow-md">
                    <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                        <div className="font-bold text-navy-500 text-sm truncate">
                            Admin / Support
                            <span className="ml-2 text-[9px] uppercase tracking-widest text-emerald-700 font-extrabold">FREE</span>
                        </div>
                    </div>
                    <div className="text-xs text-navy-500/60 truncate">Get help from GPM team — free for all users</div>
                </div>
            </button>
            {/* Community room — pinned at top */}
            <button
                onClick={() => onOpen("community")}
                data-testid="chat-community-entry"
                className={`shrink-0 w-full text-left px-4 py-3 flex items-center gap-3 border-b border-navy-500/10 transition-colors ${activeId === "community" ? "bg-gold-500/15" : "bg-gradient-to-r from-gold-500/10 to-amber-500/5 hover:bg-gold-500/15"}`}
            >
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-gold-500 to-amber-500 flex items-center justify-center shrink-0 shadow-md">
                    <Users className="w-5 h-5 text-navy-500" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                        <div className="font-bold text-navy-500 text-sm truncate">
                            Community Room
                            <span className="ml-2 text-[9px] uppercase tracking-widest text-gold-700 font-extrabold">PUBLIC</span>
                        </div>
                        {communityUnread > 0 && (
                            <span
                                data-testid="chat-community-unread"
                                className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center"
                            >{communityUnread}</span>
                        )}
                    </div>
                    <div className="text-xs text-navy-500/60 truncate">All paid subscribers — say hi!</div>
                </div>
            </button>
            <div className="flex-1 overflow-y-auto overscroll-contain" data-testid="conv-list-scroll">
                {loading && <div className="p-6 text-center text-navy-500/50 text-sm">Loading…</div>}
                {!loading && items.length === 0 && (
                    <div className="p-8 text-center">
                        <MessageSquare className="w-10 h-10 mx-auto text-navy-500/15 mb-3" />
                        <div className="font-heading text-base font-bold text-navy-500">No 1-on-1 chats yet</div>
                        <div className="text-xs text-navy-500/60 mt-1">Jump into the <strong className="text-gold-700">Community Room</strong> above, or tap <strong className="text-gold-700">+ New</strong> to start a private chat.</div>
                    </div>
                )}
                {items.map((c) => {
                    const active = c.id === activeId;
                    return (
                        <button
                            key={c.id}
                            onClick={() => onOpen(c.id)}
                            data-testid={`chat-conv-${c.id}`}
                            className={`w-full text-left px-4 py-3 flex items-center gap-3 border-b border-navy-500/5 transition-colors ${active ? "bg-gold-500/10" : "hover:bg-gold-500/5"}`}
                        >
                            <Avatar user={c.other} size={42} />
                            <div className="min-w-0 flex-1">
                                <div className="flex items-center justify-between gap-2">
                                    <div className={`text-sm ${c.unread ? "font-bold" : "font-semibold"} text-navy-500 truncate`}>{c.other?.name || "Unknown"}</div>
                                    {c.unread > 0 && (
                                        <span className="min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[10px] font-extrabold flex items-center justify-center">{c.unread}</span>
                                    )}
                                </div>
                                <div className={`text-xs truncate ${c.unread ? "text-navy-500/85 font-medium" : "text-navy-500/55"}`}>
                                    {c.last_message_preview || "No messages yet"}
                                </div>
                            </div>
                        </button>
                    );
                })}
                {/* Infinite-scroll sentinel — hits the viewport once the user scrolls near the bottom */}
                {items.length < (total || 0) && (
                    <div ref={sentinelRef} data-testid="conv-list-sentinel" className="p-4 text-center text-xs text-navy-500/50">
                        {loadingMore ? <><Loader2 className="w-4 h-4 inline animate-spin mr-1" /> Loading more…</> : "Scroll for older chats"}
                    </div>
                )}
                {items.length > 0 && items.length >= (total || items.length) && (
                    <div className="p-4 text-center text-[11px] text-navy-500/35">— End of chat list —</div>
                )}
            </div>
        </div>
    );
}

/* Message thread */
function MessageThread({ convId, meId, onBack }) {
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [messages, setMessages] = useState([]);
    const [other, setOther] = useState(null);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [text, setText] = useState("");
    const [lightbox, setLightbox] = useState(null); // resolved image URL
    const [menuFor, setMenuFor] = useState(null); // message id whose fwd/delete menu is open
    const fileRef = useRef(null);
    const bottomRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/chat/conversations/${convId}/messages`);
            setMessages(Array.isArray(data.messages) ? data.messages : []);
            setOther(data.conversation?.other || null);
            // Mark as read on load
            if (data.conversation?.unread > 0) {
                api.post(`/chat/conversations/${convId}/read`).catch(() => {});
                if (typeof window !== "undefined") window.dispatchEvent(new Event("gpm:chat:refresh"));
            }
        } catch (e) {
            toast.error("Couldn't load conversation");
        } finally {
            setLoading(false);
        }
    }, [convId]);

    useEffect(() => { setLoading(true); load(); }, [load]);
    useEffect(() => {
        const id = setInterval(load, POLL_MS);
        return () => clearInterval(id);
    }, [load]);

    useEffect(() => {
        // Auto-scroll to bottom whenever messages change
        bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
    }, [messages]);

    const send = async (imageUrl = null) => {
        const t = text.trim();
        if (!t && !imageUrl) return;
        setSending(true);
        try {
            const { data } = await api.post(`/chat/conversations/${convId}/messages`, { text: t || null, image_url: imageUrl || null });
            setMessages((prev) => [...prev, data]);
            setText("");
        } catch (err) {
            const status = err?.response?.status;
            if (status === 402) toast.error("Chat is available for subscribers. Please upgrade.");
            else toast.error(formatApiError(err.response?.data?.detail) || "Send failed");
        } finally { setSending(false); }
    };

    const onPickFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/properties/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const url = data?.url;
            if (!url) throw new Error("Upload failed");
            await send(url);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <div className="flex-1 flex flex-col bg-cream-50 min-h-0 min-w-0 w-full max-w-full overflow-hidden" data-testid={`chat-thread-${convId}`}>
            <div className="shrink-0 bg-white border-b border-navy-500/10 px-4 py-3 flex items-center gap-3">
                <button onClick={onBack} data-testid="chat-thread-back" className="md:hidden text-navy-500/60 hover:text-navy-500 p-1">
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <Avatar user={other} size={40} />
                <div className="min-w-0">
                    <div className="font-bold text-navy-500 truncate">{other?.name || "Loading…"}</div>
                    <div className="text-[11px] uppercase tracking-wider text-navy-500/50">{other?.role || ""}</div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-2" data-testid="thread-messages-scroll">
                {loading && <div className="text-center text-navy-500/50 text-sm">Loading…</div>}
                {!loading && messages.length === 0 && (
                    <div className="text-center text-navy-500/50 text-sm py-14">Say hello — start the conversation!</div>
                )}
                {messages.map((m) => {
                    const mine = m.sender_id === meId;
                    const canManage = mine && isAdmin;
                    return (
                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
                            <div className="relative group max-w-[80%]">
                                <div className={`rounded-2xl px-4 py-2 shadow-sm ${mine ? "bg-gold-500 text-navy-500 rounded-br-sm" : "bg-white text-navy-500 rounded-bl-sm border border-navy-500/10"}`}>
                                {m.image_url && (() => {
                                    const url = m.image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${m.image_url}` : m.image_url;
                                    return (
                                        <button
                                            type="button"
                                            onClick={() => setLightbox(url)}
                                            data-testid={`chat-image-thumb-${m.id}`}
                                            title="Tap to view full image"
                                            className="block rounded-lg overflow-hidden mb-1 focus:outline-none focus:ring-2 focus:ring-gold-500/60"
                                        >
                                            <img
                                                src={url}
                                                alt="attachment"
                                                className="max-w-full max-h-64 object-cover cursor-zoom-in"
                                                loading="lazy"
                                            />
                                        </button>
                                    );
                                })()}
                                {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
                                <div className={`text-[10px] mt-1 ${mine ? "text-navy-500/70" : "text-navy-500/40"}`}>
                                    {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                </div>
                            </div>
                                {canManage && (
                                    <>
                                        <button
                                            onClick={() => setMenuFor(menuFor === m.id ? null : m.id)}
                                            data-testid={`chat-msg-menu-${m.id}`}
                                            className="absolute -left-7 top-1 w-6 h-6 rounded-full bg-navy-500/10 hover:bg-navy-500/25 text-navy-500 text-[10px] font-extrabold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                            title="Message actions"
                                        >⋮</button>
                                        {menuFor === m.id && (
                                            <div className="absolute right-0 top-full mt-1 z-30 min-w-[180px] rounded-lg bg-white border border-navy-500/15 shadow-lg overflow-hidden">
                                                <button
                                                    onClick={async () => {
                                                        setMenuFor(null);
                                                        if (!window.confirm("Forward this message to ALL users?")) return;
                                                        try {
                                                            const { data } = await api.post(`/admin/chat/messages/${m.id}/forward`, { all_users: true });
                                                            toast.success(`Forwarded to ${data.sent} of ${data.total} users`);
                                                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Forward failed"); }
                                                    }}
                                                    data-testid={`chat-msg-forward-${m.id}`}
                                                    className="w-full text-left px-4 py-2 text-sm hover:bg-navy-500/5 text-navy-500 flex items-center gap-2"
                                                ><Send className="w-3.5 h-3.5" /> Forward to all users</button>
                                                <button
                                                    onClick={async () => {
                                                        setMenuFor(null);
                                                        if (!window.confirm("Delete this message?")) return;
                                                        try {
                                                            await api.delete(`/chat/messages/${m.id}`);
                                                            setMessages((prev) => prev.filter((x) => x.id !== m.id));
                                                            toast.success("Message deleted");
                                                        } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Delete failed"); }
                                                    }}
                                                    data-testid={`chat-msg-delete-${m.id}`}
                                                    className="w-full text-left px-4 py-2 text-sm hover:bg-red-500/10 text-red-600 border-t border-navy-500/10 flex items-center gap-2"
                                                ><X className="w-3.5 h-3.5" /> Delete message</button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </div>
                    );
                })}
                <div ref={bottomRef} />
            </div>

            <div className="shrink-0 bg-white border-t border-navy-500/10 px-3 py-3 flex items-end gap-2">
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onPickFile}
                    data-testid="chat-file-input"
                />
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading || sending}
                    data-testid="chat-attach-btn"
                    className="shrink-0 p-2.5 rounded-full border border-navy-500/15 text-navy-500/70 hover:text-gold-600 hover:border-gold-500/60 disabled:opacity-50"
                    aria-label="Attach image"
                >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                </button>
                <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                    }}
                    placeholder="Type a message…"
                    rows={1}
                    data-testid="chat-input"
                    className="flex-1 resize-none px-3 py-2 rounded-lg border border-navy-500/15 text-sm focus:outline-none focus:border-gold-500/60 max-h-32"
                />
                <button
                    type="button"
                    onClick={() => send()}
                    disabled={sending || (!text.trim() && !uploading)}
                    data-testid="chat-send-btn"
                    className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-gold-500 text-navy-500 font-extrabold text-sm hover:bg-gold-400 disabled:opacity-50"
                >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
            </div>
            <ImageLightbox open={!!lightbox} src={lightbox} onClose={() => setLightbox(null)} />
        </div>
    );
}

/* Community Room thread — group chat for all paid subscribers */
function CommunityThread({ meId, onBack, onRefresh }) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [text, setText] = useState("");
    const [lightbox, setLightbox] = useState(null);
    const fileRef = useRef(null);
    const bottomRef = useRef(null);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get("/chat/community/messages");
            setMessages(Array.isArray(data.messages) ? data.messages : []);
            if (onRefresh) onRefresh();
        } catch {
            toast.error("Couldn't load Community Room");
        } finally { setLoading(false); }
    }, [onRefresh]);

    useEffect(() => { setLoading(true); load(); }, [load]);
    useEffect(() => {
        const id = setInterval(load, POLL_MS);
        return () => clearInterval(id);
    }, [load]);
    useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages]);

    const send = async (imageUrl = null) => {
        const t = text.trim();
        if (!t && !imageUrl) return;
        setSending(true);
        try {
            const { data } = await api.post("/chat/community/messages", { text: t || null, image_url: imageUrl || null });
            setMessages((prev) => [...prev, data]);
            setText("");
        } catch (err) {
            const status = err?.response?.status;
            if (status === 402) toast.error("Community Room is for paid subscribers. Upgrade to post.");
            else toast.error(formatApiError(err.response?.data?.detail) || "Send failed");
        } finally { setSending(false); }
    };

    const onPickFile = async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", f);
            const { data } = await api.post("/properties/upload-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
            const url = data?.url;
            if (!url) throw new Error("Upload failed");
            await send(url);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Upload failed");
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    };

    return (
        <div className="flex-1 flex flex-col bg-cream-50 min-h-0 min-w-0 w-full max-w-full overflow-hidden" data-testid="chat-community-thread">
            <div className="shrink-0 bg-gradient-to-r from-gold-500/15 to-amber-500/5 border-b border-gold-500/30 px-4 py-3 flex items-center gap-3">
                <button onClick={onBack} data-testid="chat-community-back" className="md:hidden text-navy-500/60 hover:text-navy-500 p-1">
                    <ArrowLeft className="w-5 h-5" />
                </button>
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-gold-500 to-amber-500 flex items-center justify-center shrink-0 shadow">
                    <Users className="w-5 h-5 text-navy-500" />
                </div>
                <div className="min-w-0 flex-1">
                    <div className="font-bold text-navy-500 flex items-center gap-2">
                        Community Room
                        <span className="text-[9px] uppercase tracking-widest bg-emerald-500 text-white px-1.5 py-0.5 rounded-full">LIVE</span>
                    </div>
                    <div className="text-[11px] text-navy-500/60 truncate">All paid subscribers — casual chat, requirements, market news</div>
                </div>
            </div>

            <div className="flex-1 overflow-y-auto overscroll-contain px-4 py-4 space-y-3" data-testid="community-messages-scroll">
                {loading && <div className="text-center text-navy-500/50 text-sm">Loading community…</div>}
                {!loading && messages.length === 0 && (
                    <div className="text-center text-navy-500/50 text-sm py-14">
                        <Users className="w-10 h-10 mx-auto text-navy-500/15 mb-3" />
                        Welcome! Be the first to say hi in the Community Room.
                    </div>
                )}
                {messages.map((m) => {
                    const mine = m.sender_id === meId;
                    return (
                        <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"} gap-2`} data-testid={`chat-community-msg-${m.id}`}>
                            {!mine && (
                                <Avatar user={{ name: m.sender_name, profile_image: m.sender_avatar }} size={32} />
                            )}
                            <div className="max-w-[75%] flex flex-col">
                                {!mine && (
                                    <div className="flex items-center gap-1.5 mb-0.5 px-1">
                                        <span className="text-[11px] font-bold text-navy-500">{m.sender_name}</span>
                                        {m.sender_role && (
                                            <span className="text-[9px] uppercase tracking-widest text-gold-700 font-bold">{m.sender_role}</span>
                                        )}
                                        {m.sender_city && <span className="text-[10px] text-navy-500/50">· {m.sender_city}</span>}
                                    </div>
                                )}
                                <div className={`rounded-2xl px-4 py-2 shadow-sm ${mine ? "bg-gold-500 text-navy-500 rounded-br-sm" : "bg-white text-navy-500 rounded-bl-sm border border-navy-500/10"}`}>
                                    {m.image_url && (() => {
                                        const url = m.image_url.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${m.image_url}` : m.image_url;
                                        return (
                                            <button
                                                type="button"
                                                onClick={() => setLightbox(url)}
                                                data-testid={`chat-community-image-thumb-${m.id}`}
                                                title="Tap to view full image"
                                                className="block rounded-lg overflow-hidden mb-1 focus:outline-none focus:ring-2 focus:ring-gold-500/60"
                                            >
                                                <img
                                                    src={url}
                                                    alt="attachment"
                                                    className="max-w-full max-h-64 object-cover cursor-zoom-in"
                                                    loading="lazy"
                                                />
                                            </button>
                                        );
                                    })()}
                                    {m.text && <div className="text-sm whitespace-pre-wrap break-words">{m.text}</div>}
                                    <div className={`text-[10px] mt-1 ${mine ? "text-navy-500/70" : "text-navy-500/40"}`}>
                                        {new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
                <div ref={bottomRef} />
            </div>

            <div className="shrink-0 bg-white border-t border-navy-500/10 px-3 py-3 flex items-end gap-2">
                <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onPickFile}
                    data-testid="chat-community-file-input"
                />
                <button
                    type="button"
                    onClick={() => fileRef.current?.click()}
                    disabled={uploading || sending}
                    data-testid="chat-community-attach-btn"
                    className="shrink-0 p-2.5 rounded-full border border-navy-500/15 text-navy-500/70 hover:text-gold-600 hover:border-gold-500/60 disabled:opacity-50"
                    aria-label="Attach image"
                >
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ImageIcon className="w-4 h-4" />}
                </button>
                <textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
                    }}
                    placeholder="Say something…"
                    rows={1}
                    data-testid="chat-community-input"
                    className="flex-1 min-w-0 resize-none px-3 py-2 rounded-lg border border-navy-500/15 text-sm leading-6 focus:outline-none focus:border-gold-500/60 max-h-32"
                />
                <button
                    type="button"
                    onClick={() => send()}
                    disabled={sending || (!text.trim() && !uploading)}
                    data-testid="chat-community-send-btn"
                    className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-gold-500 text-navy-500 font-extrabold text-sm hover:bg-gold-400 disabled:opacity-50"
                >
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                </button>
            </div>
            <ImageLightbox open={!!lightbox} src={lightbox} onClose={() => setLightbox(null)} />
        </div>
    );
}

export default function Chat() {
    const { user, loading: authLoading } = useAuth();
    const { convId } = useParams();
    const nav = useNavigate();
    const [convs, setConvs] = useState([]);
    const convsRef = useRef([]);
    useEffect(() => { convsRef.current = convs; }, [convs]);
    const [convsTotal, setConvsTotal] = useState(0);
    const [convsLoadingMore, setConvsLoadingMore] = useState(false);
    const [loadingList, setLoadingList] = useState(true);
    const [newChatOpen, setNewChatOpen] = useState(false);
    const [communityUnread, setCommunityUnread] = useState(0);
    const CONV_PAGE = 30;

    useEffect(() => {
        if (!authLoading && !user) nav("/login?next=/chat");
    }, [user, authLoading, nav]);

    // Lock body + html scroll while on the chat page so only the messages area scrolls internally.
    useEffect(() => {
        const html = document.documentElement;
        const body = document.body;
        const prev = {
            htmlOverflow: html.style.overflow,
            bodyOverflow: body.style.overflow,
            htmlHeight: html.style.height,
            bodyHeight: body.style.height,
        };
        html.style.overflow = "hidden";
        body.style.overflow = "hidden";
        html.style.height = "100%";
        body.style.height = "100%";
        return () => {
            html.style.overflow = prev.htmlOverflow;
            body.style.overflow = prev.bodyOverflow;
            html.style.height = prev.htmlHeight;
            body.style.height = prev.bodyHeight;
        };
    }, []);

    const loadConvs = useCallback(async () => {
        try {
            // Refresh only what's already loaded so pagination state survives the 5s poll.
            // If nothing loaded yet, fetch the first page (CONV_PAGE).
            const currentCount = convsRef.current.length;
            // Cap the refresh limit to 200 (backend max) so we don't request unbounded pages on very deep-scrolled admins.
            const wantLimit = Math.min(200, Math.max(CONV_PAGE, currentCount));
            const res = await api.get("/chat/conversations", { params: { limit: wantLimit, offset: 0 } });
            const data = res.data;
            const list = Array.isArray(data) ? data : [];
            // Preserve the local ordering / count — replace with the refreshed slice sorted by last_message_at (backend already sorts desc).
            setConvs(list);
            const total = parseInt(res.headers?.["x-total-count"] || res.headers?.["X-Total-Count"] || "0", 10);
            if (!Number.isNaN(total)) setConvsTotal(total);
        } catch { /* silent */ }
        finally { setLoadingList(false); }
        try {
            const { data } = await api.get("/chat/community/unread-count");
            setCommunityUnread(data?.count || 0);
        } catch { /* silent */ }
    }, []);

    const loadMoreConvs = useCallback(async () => {
        if (convsLoadingMore || convs.length >= convsTotal) return;
        setConvsLoadingMore(true);
        try {
            const res = await api.get("/chat/conversations", { params: { limit: CONV_PAGE, offset: convs.length } });
            const older = Array.isArray(res.data) ? res.data : [];
            // Deduplicate by conv id — poll may have refreshed while we were paging
            setConvs((prev) => {
                const seen = new Set(prev.map((c) => c.id));
                return [...prev, ...older.filter((c) => !seen.has(c.id))];
            });
        } catch { /* silent */ }
        finally { setConvsLoadingMore(false); }
    }, [convs.length, convsTotal, convsLoadingMore]);

    useEffect(() => {
        if (!user) return;
        loadConvs();
        const id = setInterval(loadConvs, POLL_MS);
        const refresh = () => loadConvs();
        window.addEventListener("gpm:chat:refresh", refresh);
        return () => { clearInterval(id); window.removeEventListener("gpm:chat:refresh", refresh); };
    }, [user, loadConvs]);

    const onStart = async (u) => {
        try {
            const { data } = await api.post("/chat/conversations", { participant_id: u.id });
            await loadConvs();
            nav(`/chat/${data.id}`);
        } catch (err) {
            const status = err?.response?.status;
            if (status === 402) toast.error("Chat with other users is a paid feature. Upgrade — or chat with Admin/Support for FREE.");
            else toast.error(formatApiError(err.response?.data?.detail) || "Could not start chat");
        }
    };

    const startSupportChat = async () => {
        try {
            const { data } = await api.post("/chat/support");
            await loadConvs();
            nav(`/chat/${data.id}`);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail) || "Could not open Support chat");
        }
    };

    if (!user) return null;

    return (
        <>
            <Seo title="Chat — Global Property Mart" description="Free chat with Admin/Support for every user. Direct messages between users on paid plans." path="/chat" />
            {/* Mobile: fill viewport minus header (80px) + bottom nav (56px) + safety padding = 148px. Use dvh for iOS URL-bar accuracy.
                Desktop: constrained card with padding. `overflow-hidden` on this wrapper ensures only the
                messages area scrolls internally — no whole-page scroll. */}
            <div className="w-full max-w-6xl mx-auto md:my-6 md:rounded-xl md:border md:border-navy-500/10 md:shadow-sm bg-white overflow-hidden">
                <div className="flex w-full h-[calc(100dvh-148px)] md:h-[calc(100vh-8rem)] overflow-hidden">
                    {/* Left conversation list — always visible on md+; hidden on mobile when a conv is open */}
                    <div className={`${convId ? "hidden md:flex" : "flex"} flex-col w-full md:w-80 shrink-0 min-w-0 min-h-0 overflow-hidden`}>
                        <ConversationList
                            items={convs}
                            total={convsTotal}
                            activeId={convId}
                            loading={loadingList}
                            loadingMore={convsLoadingMore}
                            onLoadMore={loadMoreConvs}
                            communityUnread={communityUnread}
                            onOpen={(id) => nav(`/chat/${id}`)}
                            onNewChat={() => setNewChatOpen(true)}
                            onStartSupport={startSupportChat}
                        />
                    </div>
                    {/* Right thread — mobile shows full-screen when convId is set */}
                    <div className={`${convId ? "flex" : "hidden md:flex"} flex-1 min-w-0 max-w-full`}>
                        {convId === "community" ? (
                            <CommunityThread meId={user.id} onBack={() => nav("/chat")} onRefresh={loadConvs} />
                        ) : convId ? (
                            <MessageThread convId={convId} meId={user.id} onBack={() => nav("/chat")} />
                        ) : (
                            <div className="hidden md:flex flex-1 items-center justify-center text-center p-8">
                                <div>
                                    <MessageSquare className="w-14 h-14 mx-auto text-navy-500/15 mb-4" />
                                    <h2 className="font-heading text-xl font-bold text-navy-500">Select a conversation</h2>
                                    <p className="text-sm text-navy-500/60 mt-1">Chat with Admin/Support for FREE, or start a private chat (paid).</p>
                                    <button
                                        onClick={startSupportChat}
                                        data-testid="chat-support-cta-empty"
                                        className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold-500 text-navy-500 text-sm font-extrabold hover:bg-gold-400"
                                    >
                                        <Sparkles className="w-4 h-4" /> Chat with Support (FREE)
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <NewChatModal open={newChatOpen} onClose={() => setNewChatOpen(false)} onStart={onStart} />
        </>
    );
}
